import { BbQueueManager } from '@Components/BigBrowse/bb2/BbQueueManager';
import { HamburgerButtonPresenter } from '@Components/SideDrawerRight/HamburgerButton/HamburgerButton.presenter';
import type { FloorDM } from '@Model/FloorModels';
import type { FloorVM } from '@Model/MapModelsView';
import { getFloorVM } from '@Model/MapModelsView';
import { ObservableProp, useClassStore } from '@Utils/useClassStore';
import { inject, injectable, postConstruct } from 'inversify';
import { action, computed, observable, reaction } from 'mobx';
import { Dimensions } from 'react-native';

import container from '~ioc/ioc';
import { AnalyticsStore } from '~stores/analytics';
import AuthStore from '~stores/authentication';
import type { FloorStore } from '~stores/floor';
import { FloorsListStore } from '~stores/floors-list';
import type { AnalyticsEventData } from '~types/analytics';

@injectable()
export class FloorMapPresenter {
  @inject(HamburgerButtonPresenter)
  private _hamburgerButtonPresenter!: HamburgerButtonPresenter;
  @inject(FloorsListStore) private _floorsStore2!: FloorsListStore;
  @inject(BbQueueManager) private _bbQueueManager!: BbQueueManager;
  @inject(AuthStore) private _authStore!: AuthStore;
  @inject(AnalyticsStore) private _analyticsStore!: AnalyticsStore;

  @postConstruct() postConstruct() {
    this._hamburgerButtonPresenter.setFloorMapPresenter(this);

    reaction(
      () => this.extendedMapVisible,
      () => {
        if (this.extendedMapVisible) {
          this.resetSelectedFloor();
        }
      }
    );
  }

  public animationStates = {
    extendedMap: {
      initial: {
        opacity: 0,
        scale: 0,
        marginRight: Dimensions.get('window').width * 0.95,
        marginTop: Dimensions.get('window').height * 0.8,
      },
      animate: {
        opacity: 1,
        scale: 1,
        marginRight: 0,
        marginTop: 0,
      },
    },
  };

  @observable extendedMapVisible: boolean = false;

  @action setExtendedMapVisible = (newState: boolean = false) => {
    this.extendedMapVisible = newState;
  };

  logViewDepartmentAnalytics = (
    departmentId: number,
    departmentName: string
  ) => {
    const data: AnalyticsEventData<'view_department'> = {
      userId: this._authStore.currentUser?.id,
      departmentId,
      departmentName,
      floorId: String(this.selectedFloorId.value),
      floorName: this.selectedFloorStore?.data?.title!,
    };

    this._analyticsStore.logViewDepartment(data);
  };

  logViewFloorAnalytics = (departmentId?: number, departmentName?: string) => {
    const data: AnalyticsEventData<'view_floor'> = {
      userId: this._authStore.currentUser?.id,
      ...(Boolean(departmentId) && { departmentId }),
      ...(Boolean(departmentName) && { departmentName }),
      floorId: String(this.selectedFloorId.value),
      floorName: this.selectedFloorStore?.data?.title!,
    };

    this._analyticsStore.logViewFloor(data);
  };

  selectedFloorId = new ObservableProp<FloorDM['id'] | undefined>(undefined);

  @computed get title(): string {
    return this.selectedFloorStore?.data?.title ?? '';
  }

  @computed get selectedFloorStore(): FloorStore | undefined {
    const selectedId = this.selectedFloorId.value;
    if (!selectedId) {
      return undefined;
    }

    return this._floorsStore2.getFloorStore(selectedId);
  }

  @computed get floorsVM(): FloorVM[] {
    return this._floorsStore2.floorsDM.value.map((floorDM) =>
      getFloorVM(floorDM)
    );
  }

  @computed get reversedFloorsVM(): FloorVM[] {
    return this.floorsVM.slice().reverse();
  }

  @computed get downButtonEnabled(): boolean {
    return this.currentFloorIndex > 0;
  }

  @computed get upButtonEnabled(): boolean {
    return this.currentFloorIndex < this.floorsVM.length - 1;
  }

  goUp = () => {
    this.goFloor(this.getFloorIdUp());
  };

  goDown = () => {
    this.goFloor(this.getFloorIdDown());
  };

  goFloor = (floorId: FloorVM['id']) => {
    this.selectedFloorId.set(floorId);
    this.logFloorBrowseAnalytics(floorId);
  };

  @computed private get currentFloorIndex(): number {
    return this.floorsVM.findIndex(
      (floor) => floor.id === this.selectedFloorId.value
    );
  }

  private getFloorIdUp = (): FloorVM['id'] => {
    if (this.currentFloorIndex === -1) {
      throw new Error(
        'Floor Map error: no current floor found when trying to move across floor '
      );
    }

    const nextFloorIndex = this.currentFloorIndex + 1;
    if (nextFloorIndex === this.floorsVM.length) {
      throw new Error(
        'Floor Map error: attempted to go above the top floor index'
      );
    }

    return this.floorsVM[nextFloorIndex].id;
  };

  private getFloorIdDown = (): FloorVM['id'] => {
    if (this.currentFloorIndex === -1) {
      throw new Error(
        'Floor Map error: no current floor found when trying to move across floor '
      );
    }

    const nextFloorIndex = this.currentFloorIndex - 1;
    if (nextFloorIndex === -1) {
      throw new Error('Floor Map error: attempted to go down beyond index 0');
    }

    return this.floorsVM[nextFloorIndex].id;
  };

  private resetSelectedFloor = () => {
    this.selectedFloorId.set(this._floorsStore2.activeFloorSpecs);
  };

  private logFloorBrowseAnalytics = (floorId: FloorVM['id']) => {
    const logData: AnalyticsEventData<'browse_floor'> = {
      userId: this._authStore.currentUser?.id,
      floorId,
      floorName: this.floorsVM.find((floor) => floor.id === floorId)?.title!,
    };

    this._analyticsStore.logBrowseFloor(logData);
  };
}

export const useFloorMapPresenter = () =>
  useClassStore<FloorMapPresenter>(container.get(FloorMapPresenter));
